import axios, {
	AxiosPromise,
	AxiosRequestConfig,
	AxiosResponse,
	Method
} from 'axios';

import { IRequestHeaders, IRequestOptions, IVaporIO } from '../../interfaces';

declare const vaporio: IVaporIO;
const { retry: RETRY } = vaporio;

export const REQUEST_METHODS = {
	DELETE: 'delete',
	GET: 'get',
	PATCH: 'patch',
	POST: 'post',
	PUT: 'put'
};

// status codes that should not result in a retry
export const ACCEPTED_STATUS_CODES = [
	200,
	204,
	404,
	422
];

/**
 * This function overwrites and returns a request header JSON object using provided headers parameters.
 * @param {IRequestHeaders} [headers={}] Request header parameters
 * @returns {IRequestHeaders} Merged request header JSON
 */
export function mergeRequestHeaders(headers: IRequestHeaders = {}): IRequestHeaders {
	const requestHeaders: IRequestHeaders = {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'Content-Type': 'application/json'
	};
	const headersKeysExist = headers !== undefined && Object.keys(headers).length > 0;

	if (headersKeysExist) {
		for (const key in headers) {
			if (Object.prototype.hasOwnProperty.call(headers, key)) {
				requestHeaders[ key ] = headers[ key ];
			}
		}
	}

	return requestHeaders;
}

/**
 * This function creates a request object by merging `url`, `method`, and `options` objects into a AxiosRequestConfig object.
 * @param {string} url The url to query the request
 * @param {string} method The request method type
 * @param {IRequestOptions} [options={}] Options JSON object for the request. `{}` by default
 * @returns {AxiosRequestConfig} Axios Request Config object
 */
export function mergeRequestOptions(url: string, method: string, options: IRequestOptions = {}): AxiosRequestConfig {
	const requestObject: AxiosRequestConfig = {};
	const optionsHasKeys = Object.keys(options).length > 0;
	const optionsHasDataKey = optionsHasKeys && Object.prototype.hasOwnProperty.call(options, 'data');

	if (!optionsHasKeys || !optionsHasDataKey) {
		if (method === REQUEST_METHODS.PATCH) {
			throw new Error('patch request has no data');
		}

		if (method === REQUEST_METHODS.PUT) {
			throw new Error('put request has no data');
		}
	}

	if (optionsHasKeys) {
		if (optionsHasDataKey &&
			(method === REQUEST_METHODS.DELETE
				|| method === REQUEST_METHODS.PATCH
				|| method === REQUEST_METHODS.POST
				|| method === REQUEST_METHODS.PUT)) {
			requestObject.data = options.data;
		}

		requestObject.headers = mergeRequestHeaders(options.headers);
		requestObject.params = Object.prototype.hasOwnProperty.call(options, 'params') ? options.params : {};
	}
	else {
		requestObject.headers = {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			'Content-Type': 'application/json'
		};
	}

	// Allows axios response to successfully set the cookie in the browser using the 'set-cookie' header
	requestObject.withCredentials = true;
	requestObject.method = method as Method;
	requestObject.url = url;

	return requestObject;
}

/**
 * This function handles and returns any network request from `url` using `method` and `options` JSON object.
 * @param {string} url The url to query the request
 * @param {string} method The request method type
 * @param {IRequestOptions} [options={}] Options JSON object for the request. `{}` by default
 * @param {boolean} [retry=true] Whether to use request retry functionality or not
 * @returns {Promise<AxiosPromise>} Axios Promise with the response
 */
export async function request(
	url: string, method: string, options: IRequestOptions = {}, retry = true
): Promise<AxiosPromise> {
	if (!url || url.length < 1) {
		throw new Error('No url provided.');
	}

	if (!method || method.length < 1) {
		throw new Error('No method provided.');
	}

	const requestObject: AxiosRequestConfig = mergeRequestOptions(url, method, options);

	for (let i = 0; i < RETRY.MAX_ATTEMPTS; i += 1) {
		let response: AxiosResponse;

		try {
			response = await axios(requestObject);

			if (ACCEPTED_STATUS_CODES.includes(response?.status)) {
				return response;
			}

			throw new Error('Error performing request');
		}
		catch (e) {
			// if retry flag is disabled or if the user is unauthorized skip retry
			if (!retry || response?.status === 401) {
				return response;
			}

			if (i < RETRY.MAX_ATTEMPTS - 1) {
				await new Promise((resolve) => setTimeout(resolve, RETRY.RETRY_DELAY * i));
			}
		}
	}

	// request failed after MAX_ATTEMPTS
	throw new Error('API request failed');
}

/**
 * This function calls the DELETE request using `url` with `options` attached to it.
 * @param {string} url The url to query the request
 * @param {IRequestOptions} [options={}] Options JSON object for the request. `{}` by default
 * @param {boolean} [retry=true] Whether to use request retry functionality or not
 * @returns {Promise<AxiosPromise>} Axios Promise with the response
 */
export function remove(url: string, options: IRequestOptions = {}, retry = true): Promise<AxiosPromise> {
	return request(url, REQUEST_METHODS.DELETE, options, retry);
}

/**
 * This function calls the GET request using `url` with `options` attached to it.
 * @param {string} url The url to query the request
 * @param {IRequestOptions} [options={}] Options JSON object for the request. `{}` by default
 * @param {boolean} [retry=true] Whether to use request retry functionality or not
 * @returns {Promise<AxiosPromise>} Axios Promise with the response
 */
export function get(url: string, options: IRequestOptions = {}, retry = true): Promise<AxiosPromise> {
	return request(url, REQUEST_METHODS.GET, options, retry);
}

/**
 * This function calls the PATCH request using `url` with `options` attached to it.
 * @param {string} url The url to query the request
 * @param {IRequestOptions} [options={}] Options JSON object for the request. `{}` by default
 * @param {boolean} [retry=true] Whether to use request retry functionality or not
 * @returns {Promise<AxiosPromise>} Axios Promise with the response
 */
export function patch(url: string, options: IRequestOptions, retry = true): Promise<AxiosPromise> {
	return request(url, REQUEST_METHODS.PATCH, options, retry);
}

/**
 * This function calls the POST request using `url` with `options` attached to it.
 * @param {string} url The url to query the request
 * @param {IRequestOptions} [options={}] Options JSON object for the request. `{}` by default
 * @param {boolean} [retry=true] Whether to use request retry functionality or not
 * @returns {Promise<AxiosPromise>} Axios Promise with the response
 */
export function post(url: string, options: IRequestOptions, retry = true): Promise<AxiosPromise> {
	return request(url, REQUEST_METHODS.POST, options, retry);
}

/**
 * This function calls the PUT request using `url` with `options` attached to it.
 * @param {string} url The url to query the request
 * @param {IRequestOptions} [options={}] Options JSON object for the request. `{}` by default
 * @param {boolean} [retry=true] Whether to use request retry functionality or not
 * @returns {Promise<AxiosPromise>} Axios Promise with the response
 */
export function put(url: string, options: IRequestOptions, retry = true): Promise<AxiosPromise> {
	return request(url, REQUEST_METHODS.PUT, options, retry);
}
